/** @typedef {[number, number]} Point */

export const DEFAULT_DOOR_WIDTH_METERS = 0.9;
export const DEFAULT_DOOR_HEIGHT_METERS = 2.1;
export const DEFAULT_WINDOW_WIDTH_METERS = 1.2;
export const DEFAULT_WINDOW_HEIGHT_METERS = 1.2;
export const DEFAULT_WINDOW_SILL_HEIGHT_METERS = 0.9;
export const SEGMENT_EPS_METERS = 0.001;

/**
 * @param {Record<string, unknown>} opening
 * @param {number} wallLengthMeters
 * @returns {number | null}
 */
export function resolveOpeningT(opening, wallLengthMeters) {
  const t = Number(opening.t);
  if (Number.isFinite(t)) return Math.max(0, Math.min(1, t));
  const centerRatio = Number(opening.centerRatio);
  if (Number.isFinite(centerRatio)) return Math.max(0, Math.min(1, centerRatio));
  const offsetMeters = Number(opening.offsetMeters);
  if (Number.isFinite(offsetMeters) && wallLengthMeters > SEGMENT_EPS_METERS) {
    return Math.max(0, Math.min(1, offsetMeters / wallLengthMeters));
  }
  return null;
}

/**
 * @param {Record<string, unknown>} opening
 * @param {number} wallLengthMeters
 * @param {number} defaultWidthMeters
 * @returns {{ startM: number; endM: number; centerM: number } | null}
 */
export function openingHorizontalRangeMeters(opening, wallLengthMeters, defaultWidthMeters) {
  if (wallLengthMeters < SEGMENT_EPS_METERS) return null;
  const t = resolveOpeningT(opening, wallLengthMeters);
  if (t == null) return null;

  const widthMeters = Number(opening.widthMeters);
  const width =
    Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : defaultWidthMeters;

  const centerM = t * wallLengthMeters;
  const startM = Math.max(0, centerM - width / 2);
  const endM = Math.min(wallLengthMeters, centerM + width / 2);
  if (endM - startM < SEGMENT_EPS_METERS) return null;

  return { startM, endM, centerM };
}

/**
 * @param {Array<[number, number]>} intervals
 * @param {number} wallLength
 * @returns {Array<[number, number]>}
 */
export function mergeIntervalsMeters(intervals, wallLength) {
  const sorted = intervals
    .map(([a, b]) => [Math.max(0, Math.min(a, b)), Math.min(wallLength, Math.max(a, b))])
    .filter(([a, b]) => b - a > SEGMENT_EPS_METERS)
    .sort((left, right) => left[0] - right[0]);

  if (sorted.length === 0) return [];

  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current[0] <= last[1] + SEGMENT_EPS_METERS) {
      last[1] = Math.max(last[1], current[1]);
    } else {
      merged.push(current);
    }
  }
  return merged;
}

/**
 * @param {Array<[number, number]>} blocked
 * @param {number} wallLength
 * @returns {Array<[number, number]>}
 */
export function complementIntervalsMeters(blocked, wallLength) {
  const solid = [];
  let cursor = 0;
  for (const [start, end] of blocked) {
    if (start - cursor > SEGMENT_EPS_METERS) solid.push([cursor, start]);
    cursor = Math.max(cursor, end);
  }
  if (wallLength - cursor > SEGMENT_EPS_METERS) solid.push([cursor, wallLength]);
  return solid;
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {{ dir: { x: number; y: number }; normal: { x: number; y: number }; length: number }}
 */
export function wallDirectionNormal(start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) {
    return { dir: { x: 1, y: 0 }, normal: { x: 0, y: 1 }, length: 0 };
  }
  return {
    dir: { x: dx / length, y: dy / length },
    normal: { x: -dy / length, y: dx / length },
    length,
  };
}

/**
 * Four corners of wall footprint in 2D (same order as FloorPlanDesignerCanvas).
 * Thickness centered on centerline.
 *
 * @param {Point} start
 * @param {Point} end
 * @param {number} halfThicknessPx
 * @returns {[Point, Point, Point, Point]}
 */
export function wallFootprintCorners2D(start, end, halfThicknessPx) {
  const { normal } = wallDirectionNormal(start, end);
  const nx = normal.x * halfThicknessPx;
  const ny = normal.y * halfThicknessPx;
  return [
    [start[0] + nx, start[1] + ny],
    [end[0] + nx, end[1] + ny],
    [end[0] - nx, end[1] - ny],
    [start[0] - nx, start[1] - ny],
  ];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} t 0 at start, 1 at end
 * @returns {Point}
 */
export function pointOnWallAtT(start, end, t) {
  return [start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} centerRatio
 * @param {number} halfWidthPx
 * @returns {[Point, Point]}
 */
export function openingSpanOnWall(start, end, centerRatio, halfWidthPx) {
  const { length } = wallDirectionNormal(start, end);
  if (length < 1e-6) return [start, end];
  const centerDist = centerRatio * length;
  const startDist = Math.max(0, centerDist - halfWidthPx);
  const endDist = Math.min(length, centerDist + halfWidthPx);
  const { dir } = wallDirectionNormal(start, end);
  return [
    [start[0] + dir.x * startDist, start[1] + dir.y * startDist],
    [start[0] + dir.x * endDist, start[1] + dir.y * endDist],
  ];
}

/**
 * @param {[Point, Point, Point, Point]} corners
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {(point: Point, center: { x: number; y: number }, mpp: number) => { x: number; z: number }} imagePointToWorld
 * @returns {Array<{ x: number; z: number }>}
 */
export function footprintCornersToWorldXZ(corners, center, metersPerPixel, imagePointToWorld) {
  return corners.map((pt) => imagePointToWorld(pt, center, metersPerPixel));
}

/**
 * Flatten footprint corners for Konva Line `points` prop.
 * @param {[Point, Point, Point, Point]} corners
 * @returns {number[]}
 */
export function footprintCornersToLinePoints(corners) {
  return corners.flatMap((p) => p);
}
